import PropTypes from "prop-types";
import classNames from "classnames";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import isEprEditable from "../../../utils/ActionControls/isEprEditable";
import { getColorClass, RATING_OPTIONS, RATING_DISPLAY_LABELS } from "../../../utils/teacher/InputRatingUtils";

/**
 * The `<tbody>` of the scrollable ratings/comments side of RatingGrid.
 *
 * Purely presentational — renders one of two views:
 *   - showComments: a single textarea per student for subject-level comments
 *   - default: per-cell rating selects with comment icons
 *
 * All handlers come from the parent.
 */
const RatingGridBody = ({
    activeColumnIndex,
    comments,
    contentItems,
    derivedExistingRatingsData,
    fluencyItems,
    getFluencyWpm,
    getGradingCriteriaId,
    groupData,
    handleCommentChange,
    handleFluencyWpmChange,
    handleOpenCommentModal,
    handleRatingChange,
    hasChanges,
    imagesLoading,
    isActiveWindow,
    mode,
    ratings,
    ratingsLoaded,
    selectedEpr,
    selectedTemplate,
    showComments,
    students,
    userDetails
}) => {
    const fluencyOffset = fluencyItems.length;
    if (showComments) {
        return (
            <tbody>
                {students.map((student) => {
                    const profileStatus = mode === "GROUPS" ? student.gradingProfileStatus : selectedEpr?.status;
                    const canEdit = isEprEditable({ isActiveWindow, status: profileStatus, userDetails });
                    const gradingCriteriaId = getGradingCriteriaId(
                        student,
                        groupData,
                        selectedEpr,
                        selectedTemplate,
                        mode
                    );
                    return (
                        <tr key={student.key}>
                            <td colSpan={contentItems.length + fluencyOffset}>
                                <textarea
                                    className="student-criteria-comment-input"
                                    disabled={student.isDisabled || !canEdit}
                                    style={{ width: "100%" }}
                                    value={comments[`${student.key}_${gradingCriteriaId}`] || ""}
                                    onChange={(e) =>
                                        handleCommentChange(student.key, e.target.value, gradingCriteriaId)
                                    }
                                />
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        );
    }

    return (
        <tbody>
            {students.map((student) => {
                const profileStatus = mode === "GROUPS" ? student.gradingProfileStatus : selectedEpr?.status;
                const canEdit = isEprEditable({ isActiveWindow, status: profileStatus, userDetails });
                return (
                    <tr
                        key={student.key}
                        className={student.isShared && student.groupPermission === "VIEW" ? "view-only-row" : ""}
                    >
                        {fluencyItems.map((item, index) => (
                            <td className={index === activeColumnIndex ? "active-rating-col" : ""} key={item.id}>
                                <div className="fluency-wpm-grid-cell">
                                    <input
                                        aria-label={`${item.label} for ${student.firstName} ${student.lastName}`}
                                        className="fluency-wpm-grid-input"
                                        disabled={student.isDisabled || imagesLoading || !canEdit}
                                        inputMode={item.digitsOnly ? "numeric" : "text"}
                                        maxLength={item.maxLength || 3}
                                        onChange={(e) => handleFluencyWpmChange(student, item, e.target.value)}
                                        placeholder={item.placeholder || ""}
                                        type="text"
                                        value={getFluencyWpm(student, item)}
                                    />
                                    {item.unit ? <span className="fluency-wpm-grid-unit">{item.unit}</span> : null}
                                </div>
                            </td>
                        ))}
                        {contentItems.map((item, index) => {
                            const cellKey = `${student.key}_${item.templateContentId}`;
                            const value = ratings[cellKey] || "";
                            const existingRating = derivedExistingRatingsData
                                .find((r) => r.key === student.key)
                                ?.contentRatings?.find((cr) => cr.templateContentId === item.templateContentId);
                            const commentExists = existingRating?.comment?.trim();
                            const isNew = !student.contentRatings.some(
                                (cr) => cr.templateContentId === item.templateContentId
                            );
                            const showStatic = student.isDisabled || value === "EXCLUDED" || !canEdit;
                            const isDisabled = student.isDisabled || imagesLoading || !ratingsLoaded || !canEdit;

                            return (
                                <td
                                    key={item.templateContentId}
                                    className={index + fluencyOffset === activeColumnIndex ? "active-rating-col" : ""}
                                >
                                    <div className="rating-cell">
                                        {showStatic ? (
                                            <div className={classNames("view-only-rating", getColorClass(value))}>
                                                {RATING_DISPLAY_LABELS[value] || value || "Not Yet Rated"}
                                            </div>
                                        ) : (
                                            <select
                                                aria-label={`Rating for ${student.firstName} ${student.lastName} on ${item.body}`}
                                                className={classNames(getColorClass(value))}
                                                disabled={isDisabled}
                                                value={value}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    handleRatingChange(
                                                        student.key,
                                                        item.templateContentId,
                                                        val === "__CLEAR__" ? null : val || null
                                                    );
                                                }}
                                            >
                                                <option value="">Select...</option>
                                                {RATING_OPTIONS.map((opt) => (
                                                    <option key={opt} value={opt}>
                                                        {RATING_DISPLAY_LABELS[opt] || opt}
                                                    </option>
                                                ))}
                                                <option value="__CLEAR__">Clear Ratings</option>
                                            </select>
                                        )}
                                        {commentExists ? (
                                            <OverlayTrigger
                                                placement="top"
                                                overlay={
                                                    <Tooltip id={`tooltip-${student.key}-${item.templateContentId}`}>
                                                        {commentExists.length > 100
                                                            ? commentExists.slice(0, 100) + "..."
                                                            : commentExists}
                                                    </Tooltip>
                                                }
                                            >
                                                <button
                                                    type="button"
                                                    className={classNames("comment-icon bi bi-chat-left-dots-fill", {
                                                        "comment-icon--disabled": isDisabled
                                                    })}
                                                    aria-label={isDisabled ? "View Comment" : "Edit Comment"}
                                                    disabled={isDisabled}
                                                    onClick={() => handleOpenCommentModal(student, item)}
                                                    style={{ border: "none", background: "none", padding: 0 }}
                                                />
                                            </OverlayTrigger>
                                        ) : (
                                            student.groupPermission !== "VIEW" &&
                                            !isNew &&
                                            value !== "EXCLUDED" && (
                                                <button
                                                    type="button"
                                                    className={classNames(
                                                        `${getColorClass(value)}-icon`,
                                                        "comment-icon",
                                                        "bi bi-chat-left-dots",
                                                        { "comment-icon--disabled": isDisabled }
                                                    )}
                                                    aria-label={isDisabled ? "Comments disabled" : "Add comment"}
                                                    disabled={isDisabled || (isNew && hasChanges)}
                                                    onClick={() => handleOpenCommentModal(student, item)}
                                                    style={{ border: "none", background: "none", padding: 0 }}
                                                />
                                            )
                                        )}
                                    </div>
                                </td>
                            );
                        })}
                    </tr>
                );
            })}
        </tbody>
    );
};

RatingGridBody.propTypes = {
    activeColumnIndex: PropTypes.number,
    comments: PropTypes.object.isRequired,
    contentItems: PropTypes.array.isRequired,
    derivedExistingRatingsData: PropTypes.array.isRequired,
    fluencyItems: PropTypes.array,
    getFluencyWpm: PropTypes.func,
    getGradingCriteriaId: PropTypes.func.isRequired,
    groupData: PropTypes.array,
    handleCommentChange: PropTypes.func.isRequired,
    handleFluencyWpmChange: PropTypes.func,
    handleOpenCommentModal: PropTypes.func.isRequired,
    handleRatingChange: PropTypes.func.isRequired,
    hasChanges: PropTypes.bool.isRequired,
    imagesLoading: PropTypes.bool,
    isActiveWindow: PropTypes.bool,
    mode: PropTypes.string.isRequired,
    ratings: PropTypes.object.isRequired,
    ratingsLoaded: PropTypes.bool,
    selectedEpr: PropTypes.object,
    selectedTemplate: PropTypes.object.isRequired,
    showComments: PropTypes.bool.isRequired,
    students: PropTypes.array.isRequired,
    userDetails: PropTypes.object
};

RatingGridBody.defaultProps = {
    fluencyItems: [],
    getFluencyWpm: () => "",
    handleFluencyWpmChange: () => {},
    imagesLoading: false,
    ratingsLoaded: true,
    selectedEpr: null,
    groupData: []
};

export default RatingGridBody;
